import { Button, FormHeading, FormSectionGroup, Layout } from '@vwfs-bronson/bronson-react';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AddressFormNew } from '../../../../../../components/AddressFormNew';
import { BankDetailsNew } from '../../../../../../components/BankDetailsNew';
import { CompanyDetailsForm_OB_OL } from '../../../../../../components/CompanyDetailsFormNew/business/otherBusiness/operatingLease/CompanyDetailsForm_OB_OL';
import { ContactPersonForm_OB_OL } from '../../../../../../components/ContactPersonForm/business/otherBusiness/operatingLease/ContactPersonForm_OB_OL';
import { DirectorPersonForm_OB_OL } from '../../../../../../components/DirectorPersonForm/business/otherBusiness/DirectorPersonForm_OB_OL';
import { LinkedRelationshipsForm_OB_OL } from '../../../../../../components/LinkedRelationshipsNew/business/otherBusiness/LinkedRelationships_OB_OL';
import { journeyDataSelectors } from '../../../../../../services/redux/features/journeyData.slice';
import { useFormSectionHelpers } from '../../../../../utils';

const getCustomerFormsToRender = (showBankDetails: boolean, isAnotherHirerAdded: boolean) => {
  return [
    'companyDetailsForm',
    'addressFormRoi',
    'companyAddress',
    'contactPersonForm',
    ...(showBankDetails ? (['bankDetails'] as const) : []),
    'directorPersonForm1',
    'addressForm1',
    'linkedRelationshipsForm1',
    ...(isAnotherHirerAdded ? (['directorPersonForm2', 'addressForm2', 'linkedRelationshipsForm2'] as const) : []),
  ] as const;
};

type FormId = ReturnType<typeof getCustomerFormsToRender>[number];

/**
 * - OtherBusiness (LTD, PLC, Other, Unlimited, Partnership)
 * - OperatingLease
 */
export const CustomerDataOperatingLeaseOtherBusiness = () => {
  const [forms, setForms] = useState<ReadonlyArray<FormId>>(() => getCustomerFormsToRender(false, false));
  const [isAnotherHirerAdded, setIsAnotherHirerAdded] = useState(false);

  const toggleAnotherHirer = () => {
    setIsAnotherHirerAdded((isAnotherHirerAdded) => !isAnotherHirerAdded);
  };

  const isUpfrontPayment = useSelector(journeyDataSelectors.isUpfrontPayment);

  useEffect(() => {
    setForms([...getCustomerFormsToRender(!isUpfrontPayment, isAnotherHirerAdded)]);
  }, [isUpfrontPayment, isAnotherHirerAdded]);

  const getFormHelpers = useFormSectionHelpers(forms);

  const businessCustomerType = useSelector(journeyDataSelectors.businessCustomerType);

  // Helper for some more variable titles
  const getTitleTranslation = <T extends string>(fileName: T, isAnotherHirer = false): `${T}:title:${string}` => {
    return `${fileName}:title:${businessCustomerType}${isAnotherHirer ? 2 : 1}`;
  };

  // Returns the correct translation key for the add/remove additional hirer button
  const getAnotherHirerButtonTranslationKey = (): string => {
    const nonDirectorMap: Record<string, string> = {
      partnership: 'Partner',
      other: 'Other',
    };

    const mappedType = nonDirectorMap[businessCustomerType!] ?? 'Director';
    const action = isAnotherHirerAdded ? 'removeAnother' : 'addAnother';

    return `customerDataPrivate:${action}${mappedType}Button`;
  };

  return (
    <FormSectionGroup>
      <CompanyDetailsForm_OB_OL formHelpers={getFormHelpers('companyDetailsForm')} />

      <AddressFormNew.OL
        formHelpers={getFormHelpers('addressFormRoi')}
        titleTranslationKey="addressForm:title:roiBranch"
        showCountry={false}
      />

      <AddressFormNew.OL
        formHelpers={getFormHelpers('companyAddress')}
        titleTranslationKey="addressForm:title:business"
        showCountry
      />

      <ContactPersonForm_OB_OL formHelpers={getFormHelpers('contactPersonForm')} />

      <BankDetailsNew.OL
        formHelpers={getFormHelpers('bankDetails')}
        titleTranslationKey="bankDetailsForm:title:business"
      />

      <DirectorPersonForm_OB_OL
        formHelpers={getFormHelpers('directorPersonForm1')}
        titleTranslationKey={getTitleTranslation('directorPersonForm')}
      />

      <AddressFormNew.OL
        formHelpers={getFormHelpers('addressForm1')}
        titleTranslationKey={getTitleTranslation('addressForm')}
        showCountry
      />

      <LinkedRelationshipsForm_OB_OL
        formHelpers={getFormHelpers('linkedRelationshipsForm1')}
        titleTranslationKey={getTitleTranslation('linkedRelationshipsForm')}
      />

      {isAnotherHirerAdded && (
        <>
          <Layout>
            <Layout.Item default="1/2">
              <FormHeading>{t(`customerDataPage:customer2Header:${businessCustomerType}`)}</FormHeading>
            </Layout.Item>
          </Layout>

          <DirectorPersonForm_OB_OL
            formHelpers={getFormHelpers('directorPersonForm2')}
            titleTranslationKey={getTitleTranslation('directorPersonForm', true)}
          />

          <AddressFormNew.OL
            formHelpers={getFormHelpers('addressForm2')}
            titleTranslationKey={getTitleTranslation('addressForm', true)}
            showCountry
          />

          <LinkedRelationshipsForm_OB_OL
            formHelpers={getFormHelpers('linkedRelationshipsForm2')}
            titleTranslationKey={getTitleTranslation('linkedRelationshipsForm', true)}
          />
        </>
      )}

      <Layout className="u-mb">
        <Layout.Item>
          <Button
            icon={isAnotherHirerAdded ? 'semantic-minus' : 'semantic-plus'}
            link
            secondary
            onClick={toggleAnotherHirer}
          >
            {t(getAnotherHirerButtonTranslationKey())}
          </Button>
        </Layout.Item>
      </Layout>
    </FormSectionGroup>
  );
};
